import { useQuery } from "@tanstack/react-query";
import { differenceInDays } from "date-fns";
import type React from "react";
import { useState } from "react";

import type { PlatformReachProjectDto } from "@/api/types";
import { SegmentGroup } from "@/design-system/SegmentGroup";
import { Skeleton } from "@/design-system/Skeleton";
import { Stack } from "@/design-system/Stack";
import { MAX_ANALYTICS_PERCENTAGE_VALUE } from "@/helpers/analytics";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { usePortfolioAnalyticsQueries } from "@/queries/portfolio-analytics/queryOptions";
import { useTranslation } from "@/translations";

import { RingChart } from "../Charts/RingChart";
import { Mail01Icon } from "../Icons/Mail01Icon";
import { AnalyticsWidgetCard } from "./AnalyticsWidgetCard";
import { AnalyticsWidgetProjectList } from "./AnalyticsWidgetProjectList";
import {
  derivePrimaryDateRangeFromPeriod,
  deriveSecondaryDateRangeFromPeriod,
  formatAnalyticsDatePayload,
  formatAnalyticsPercentageValue,
} from "./helpers";
import type { AnalyticsWidgetBaseProps } from "./types";
import { useMetricHelp } from "./useMetricHelp";

type PlatformReachWidgetProps = AnalyticsWidgetBaseProps;

export function PlatformReachWidget({ projectIds = [], period }: PlatformReachWidgetProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const [selectedProjectSegment, setSelectedProjectSegment] = useState<"positive" | "negative">("positive");

  const { t } = useTranslation();
  const help = useMetricHelp();
  const portfolioAnalyticsQueries = usePortfolioAnalyticsQueries();

  const secondaryDates = deriveSecondaryDateRangeFromPeriod(period);
  const { data: secondaryData } = useQuery(
    portfolioAnalyticsQueries.platformReach({
      projectId: projectIds.length === 1 ? projectIds[0] : undefined,
      startDate: formatAnalyticsDatePayload(secondaryDates.startDate),
      endDate: formatAnalyticsDatePayload(secondaryDates.endDate),
    }),
  );

  const primaryDates = derivePrimaryDateRangeFromPeriod(period);
  const { data: primaryData, isPending } = useQuery({
    ...portfolioAnalyticsQueries.platformReach({
      projectId: projectIds.length === 1 ? projectIds[0] : undefined,
      startDate: primaryDates.startDate ? formatAnalyticsDatePayload(primaryDates.startDate) : undefined,
      endDate: primaryDates.endDate ? formatAnalyticsDatePayload(primaryDates.endDate) : undefined,
    }),
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        ...data,
        projects: data.projects.filter((project) => projectIds.includes(project.projectId)),
      };
    },
  });

  const aggregateData = (projects: PlatformReachProjectDto[]) => {
    return (projects ?? []).reduce(
      (acc, project) => {
        if (project.totalAddressCount) {
          acc.amountTotalAddresses += project.totalAddressCount;
          acc.amountReachableAddresses += project.reachableAddressCount;
          acc.amountAdminPost += project.adminPostCount ?? 0;
        }

        return acc;
      },
      {
        amountReachableAddresses: 0,
        amountTotalAddresses: 0,
        amountAdminPost: 0,
      },
    );
  };

  const computeScore = (amountReachableAddresses: number, amountTotalAddresses: number) => {
    return amountTotalAddresses > 0 ? (amountReachableAddresses / amountTotalAddresses) * 100 : 0;
  };

  const primaryProjects = primaryData?.projects ?? [];
  const primaryAggregatedData = aggregateData(primaryProjects);
  const platformReachScore = computeScore(
    primaryAggregatedData.amountReachableAddresses,
    primaryAggregatedData.amountTotalAddresses,
  );

  return (
    <AnalyticsWidgetCard
      title={t("component.analytics-widget.platform-reach.title")}
      titleTooltip={help("component.analytics-widget.platform-reach.tooltip")}
      icon={<Mail01Icon />}
      isLoading={isPending}
      renderSkeleton={() => <WidgetSkeleton />}
    >
      <section className="flex flex-1 flex-col items-center justify-center gap-space-md text-center">
        <RingChart
          value={platformReachScore}
          max={MAX_ANALYTICS_PERCENTAGE_VALUE}
          renderValue={formatAnalyticsPercentageValue}
        />
        <h4 className="text-heading-4">
          {t("component.analytics-widget.platform-reach.subtitle", {
            totalAddressCount: primaryAggregatedData.amountTotalAddresses,
            reachableAddressCount: primaryAggregatedData.amountReachableAddresses,
          })}
        </h4>
      </section>
      {primaryData && primaryData.projects.length === 1 && (
        <Stack>
          <section className="flex w-full flex-col gap-space-md border-t border-t-border-default py-space-md">
            {/* Operational communication activity */}
            <span className="text-body-emphasis">
              {t("component.analytics-widget.platform-reach.communication-activity.subtitle")}
            </span>
            <Stack gap="sm">
              <span className="text-figure-lg text-text-brand-secondary">
                {t("component.analytics-widget.platform-reach.communication-activity.figure", {
                  adminPostCount: primaryAggregatedData.amountAdminPost,
                })}
              </span>
              <p className="text-caption text-text-secondary">
                {t("component.analytics-widget.platform-reach.communication-activity.helper-text")}
              </p>
            </Stack>
          </section>
          {primaryProjects[0].adminsLastPostedAt && (
            <section className="flex w-full flex-col gap-space-md border-t border-t-border-default py-space-md">
              <span className="text-body-emphasis">
                {t("component.analytics-widget.platform-reach.last-communication-update.subtitle")}
              </span>
              <Stack gap="sm">
                <span className="text-figure-lg text-text-brand-secondary">
                  {t("component.analytics-widget.platform-reach.last-communication-update.figure", {
                    amountDaysSinceLastAdminPost: differenceInDays(clockDate, primaryProjects[0].adminsLastPostedAt),
                  })}
                </span>
                <p className="text-caption text-text-secondary">
                  {t("component.analytics-widget.platform-reach.last-communication-update.helper-text")}
                </p>
              </Stack>
            </section>
          )}
        </Stack>
      )}
      {primaryData && primaryData.projects.length > 1 && (
        <>
          <SegmentGroup
            value={selectedProjectSegment}
            onChange={(value) => setSelectedProjectSegment(value)}
            items={[
              {
                label: t("component.analytics-widget.project-segment-group.label.top-performing"),
                value: "positive",
              },
              {
                label: t("component.analytics-widget.project-segment-group.label.bottom-performing"),
                value: "negative",
              },
            ]}
          />
          <AnalyticsWidgetProjectList
            scoreFormat="percentage"
            order={selectedProjectSegment === "positive" ? "descending" : "ascending"}
            projects={(primaryData?.projects ?? []).map((currPrimaryProject) => {
              const currSecondaryProject = secondaryData?.projects.find(
                (currProject) => currProject.projectId === currPrimaryProject.projectId,
              );

              return {
                projectId: currPrimaryProject.projectId,
                projectName: currPrimaryProject.projectName,
                projectLogo: currPrimaryProject.projectLogo,
                score: computeScore(currPrimaryProject.reachableAddressCount, currPrimaryProject.totalAddressCount),
                referenceScore: currSecondaryProject
                  ? computeScore(currSecondaryProject.reachableAddressCount, currSecondaryProject.totalAddressCount)
                  : undefined,
              };
            })}
          />
        </>
      )}
    </AnalyticsWidgetCard>
  );
}

function WidgetSkeleton(): React.ReactNode {
  return (
    <Stack gap="md" align="center">
      <Skeleton height="40" width="40" shape="roundedTop" />
      <Skeleton height="6" width="1/2" />
      <Skeleton height="8" />
    </Stack>
  );
}
